// ================================
// VIRTUAL CLASSROOM INTERFACE
// JavaScript Functionality
// ================================

import '../styles/classroom.css';
import * as THREE from 'three';
import { gsap } from 'gsap';

// ================================
// STATE MANAGEMENT
// ================================
const state = {
  classroomActive: true,
  neuralMetrics: {
    focus: 87,
    engagement: 92,
    retention: 94
  },
  tools: {
    hologram: false,
    collab: false,
    notes: false,
    translate: false,
    quiz: false,
    'ai-tutor': false
  },
  controls: {
    hand: false,
    share: false,
    breakout: false,
    immerse: true
  },
  hologramModalOpen: false
};

// ================================
// THREE.JS CLASSROOM SCENE
// ================================
function initClassroomScene() {
  const canvas = document.getElementById('classroom-canvas');
  if (!canvas) return;
  
  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog(0x0a0a0f, 10, 50);
  
  const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  
  camera.position.set(0, 3, 10);
  camera.lookAt(0, 2, 0);
  
  // Create classroom floor grid
  const gridHelper = new THREE.GridHelper(30, 30, 0x00f3ff, 0x00f3ff);
  gridHelper.material.opacity = 0.2;
  gridHelper.material.transparent = true;
  scene.add(gridHelper);
  
  // Create holographic displays
  const displays = [];
  const displayPositions = [
    { x: -8, y: 3, z: -5 },
    { x: 0, y: 3, z: -8 },
    { x: 8, y: 3, z: -5 }
  ];
  
  displayPositions.forEach((pos, index) => {
    // Display screen
    const displayGeometry = new THREE.PlaneGeometry(4, 3);
    const displayMaterial = new THREE.MeshBasicMaterial({
      color: 0x00f3ff,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    const display = new THREE.Mesh(displayGeometry, displayMaterial);
    display.position.set(pos.x, pos.y, pos.z);
    display.lookAt(0, 3, 10);
    
    // Display frame
    const frameGeometry = new THREE.EdgesGeometry(displayGeometry);
    const frameMaterial = new THREE.LineBasicMaterial({ color: 0x00f3ff });
    const frame = new THREE.LineSegments(frameGeometry, frameMaterial);
    frame.position.copy(display.position);
    frame.lookAt(0, 3, 10);
    
    scene.add(display);
    scene.add(frame);
    displays.push({ display, frame });
  });
  
  // Create floating data particles
  const particlesGeometry = new THREE.BufferGeometry();
  const particleCount = 1000;
  const positions = new Float32Array(particleCount * 3);
  const colors = new Float32Array(particleCount * 3);
  
  const color1 = new THREE.Color(0x00f3ff);
  const color2 = new THREE.Color(0xff00ff);
  const color3 = new THREE.Color(0x8000ff);
  
  for (let i = 0; i < particleCount * 3; i += 3) {
    positions[i] = (Math.random() - 0.5) * 30;
    positions[i + 1] = Math.random() * 10;
    positions[i + 2] = (Math.random() - 0.5) * 30;
    
    const colorChoice = Math.random();
    const chosenColor = colorChoice < 0.33 ? color1 : colorChoice < 0.66 ? color2 : color3;
    colors[i] = chosenColor.r;
    colors[i + 1] = chosenColor.g;
    colors[i + 2] = chosenColor.b;
  }
  
  particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  
  const particlesMaterial = new THREE.PointsMaterial({
    size: 0.05,
    vertexColors: true,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending
  });
  
  const particles = new THREE.Points(particlesGeometry, particlesMaterial);
  scene.add(particles);
  
  // Create neural network visualization
  const neuralGeometry = new THREE.BufferGeometry();
  const neuralVertices = [];
  const neuralCount = 50;
  
  for (let i = 0; i < neuralCount; i++) {
    const angle = (i / neuralCount) * Math.PI * 2;
    const radius = 5;
    neuralVertices.push(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius + 5,
      Math.sin(angle * 2) * 2
    );
  }
  
  neuralGeometry.setAttribute('position', new THREE.Float32BufferAttribute(neuralVertices, 3));
  const neuralMaterial = new THREE.LineBasicMaterial({
    color: 0x8000ff,
    transparent: true,
    opacity: 0.5
  });
  const neuralLines = new THREE.LineLoop(neuralGeometry, neuralMaterial);
  scene.add(neuralLines);
  
  // Lighting
  const ambientLight = new THREE.AmbientLight(0x404040, 1);
  scene.add(ambientLight);
  
  const pointLight1 = new THREE.PointLight(0x00f3ff, 2, 20);
  pointLight1.position.set(0, 8, 0);
  scene.add(pointLight1);
  
  const pointLight2 = new THREE.PointLight(0xff00ff, 1, 15);
  pointLight2.position.set(-10, 5, -10);
  scene.add(pointLight2);
  
  const pointLight3 = new THREE.PointLight(0x8000ff, 1, 15);
  pointLight3.position.set(10, 5, -10);
  scene.add(pointLight3);
  
  // Animation loop
  let time = 0;
  function animate() {
    requestAnimationFrame(animate);
    time += 0.01;
    
    // Rotate displays slightly
    displays.forEach((displayObj, index) => {
      displayObj.display.rotation.y = Math.sin(time + index) * 0.1;
      displayObj.frame.rotation.y = Math.sin(time + index) * 0.1;
      displayObj.display.material.opacity = 0.2 + Math.sin(time * 2 + index) * 0.1;
    });
    
    // Animate particles
    particles.rotation.y += 0.001;
    const positions = particles.geometry.attributes.position.array;
    for (let i = 1; i < positions.length; i += 3) {
      positions[i] += Math.sin(time + i) * 0.01;
      if (positions[i] > 10) positions[i] = 0;
    }
    particles.geometry.attributes.position.needsUpdate = true;
    
    // Animate neural network
    neuralLines.rotation.y += 0.005;
    neuralLines.rotation.z = Math.sin(time) * 0.2;
    
    // Camera gentle movement
    camera.position.x = Math.sin(time * 0.3) * 2;
    camera.position.y = 3 + Math.sin(time * 0.2) * 0.5;
    camera.lookAt(0, 2, 0);
    
    renderer.render(scene, camera);
  }
  animate();
  
  // Handle window resize
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });
  
  return { scene, camera, renderer, displays, particles };
}

// ================================
// NEURAL METRICS ANIMATION
// ================================
function animateNeuralMetrics() {
  const metrics = document.querySelectorAll('.metric');
  
  metrics.forEach((metric, index) => {
    const fill = metric.querySelector('.metric-fill');
    const valueElement = metric.querySelector('.metric-value');
    const metricName = metric.querySelector('.metric-label').textContent.toLowerCase();
    
    // Get metric value from state
    const metricKey = metricName.split(' ')[0]; // 'focus', 'engagement', 'retention'
    let targetValue = state.neuralMetrics[metricKey] || 85;
    
    // Animate on load
    gsap.fromTo(fill,
      { width: '0%' },
      {
        width: `${targetValue}%`,
        duration: 1.5,
        delay: index * 0.2,
        ease: 'power2.out'
      }
    );
    
    // Update values periodically
    setInterval(() => {
      targetValue = Math.max(70, Math.min(100, targetValue + (Math.random() - 0.5) * 5));
      state.neuralMetrics[metricKey] = Math.round(targetValue);
      
      gsap.to(fill, {
        width: `${targetValue}%`,
        duration: 0.5
      });
      
      if (valueElement) {
        valueElement.textContent = `${Math.round(targetValue)}%`;
      }
    }, 5000);
  });
}

// ================================
// LEARNING TOOLS
// ================================
function initLearningTools() {
  const toolButtons = document.querySelectorAll('.tool-btn');
  
  toolButtons.forEach(btn => {
    btn.addEventListener('click', function() {
      const toolName = this.querySelector('.tool-label').textContent.toLowerCase().replace(' ', '-');
      
      // Toggle active state
      this.classList.toggle('active');
      state.tools[toolName] = this.classList.contains('active');
      
      // Animate button
      gsap.fromTo(this,
        { scale: 1 },
        { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1 }
      );
      
      // Handle specific tools
      if (toolName === 'hologram-3d') {
        openHologramModal();
      } else {
        showNotification(`${toolName.replace('-', ' ')} ${state.tools[toolName] ? 'activated' : 'deactivated'}`);
      }
      
      console.log(`Tool ${toolName}:`, state.tools[toolName]);
    });
  });
}

// ================================
// CONTROL BUTTONS
// ================================
function initControlButtons() {
  const controlButtons = document.querySelectorAll('.control-btn');
  
  controlButtons.forEach(btn => {
    btn.addEventListener('click', function() {
      const btnText = this.textContent.trim();
      
      if (this.classList.contains('leave')) {
        handleLeaveClassroom();
        return;
      }
      
      // Toggle active state
      this.classList.toggle('active');
      
      // Animate button
      gsap.fromTo(this,
        { scale: 1 },
        { scale: 0.9, duration: 0.1, yoyo: true, repeat: 1 }
      );
      
      const controlName = btnText.toLowerCase();
      if (state.controls.hasOwnProperty(controlName)) {
        state.controls[controlName] = this.classList.contains('active');
      }
      
      showNotification(`${controlName.charAt(0).toUpperCase() + controlName.slice(1)} ${this.classList.contains('active') ? 'enabled' : 'disabled'}`);
      
      console.log(`Control ${controlName}:`, this.classList.contains('active'));
    });
  });
}

// ================================
// HOLOGRAM MODAL
// ================================
function initHologramModal() {
  const modal = document.getElementById('hologram-modal');
  const closeBtn = document.querySelector('.modal-close');
  const hologramButtons = document.querySelectorAll('.hologram-controls .modal-btn');
  
  if (!modal) return;
  
  closeBtn?.addEventListener('click', closeHologramModal);
  
  // Close on outside click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeHologramModal();
    }
  });
  
  // Hologram control buttons
  hologramButtons.forEach(btn => {
    btn.addEventListener('click', function() {
      const action = this.textContent.trim().toLowerCase();
      
      gsap.fromTo(this,
        { scale: 1 },
        { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1 }
      );
      
      showNotification(`Hologram ${action}`);
      console.log(`Hologram action: ${action}`);
    });
  });
}

function openHologramModal() {
  const modal = document.getElementById('hologram-modal');
  if (!modal) return;
  
  modal.classList.add('active');
  state.hologramModalOpen = true;
  
  // Animate modal content
  const modalContent = modal.querySelector('.modal-content');
  gsap.fromTo(modalContent,
    { scale: 0.8, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.7)' }
  );
}

function closeHologramModal() {
  const modal = document.getElementById('hologram-modal');
  if (!modal) return;
  
  const modalContent = modal.querySelector('.modal-content');
  gsap.to(modalContent, {
    scale: 0.8,
    opacity: 0,
    duration: 0.3,
    onComplete: () => {
      modal.classList.remove('active');
      state.hologramModalOpen = false;
    }
  });
}

// ================================
// LEAVE CLASSROOM
// ================================
function handleLeaveClassroom() {
  const confirmation = confirm('Are you sure you want to leave the classroom?');
  if (!confirmation) return;
  
  // Show leaving animation
  const body = document.body;
  gsap.to(body, {
    opacity: 0,
    duration: 0.5,
    onComplete: () => {
      window.location.href = '/';
    }
  });
}

// ================================
// NOTIFICATIONS
// ================================
function showNotification(message) {
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 90px;
    left: 50%;
    transform: translateX(-50%) translateY(-20px);
    background: rgba(0, 243, 255, 0.2);
    border: 1px solid rgba(0, 243, 255, 0.5);
    border-radius: 15px;
    padding: 15px 30px;
    color: white;
    font-family: 'Orbitron', sans-serif;
    backdrop-filter: blur(10px);
    z-index: 10000;
    opacity: 0;
    pointer-events: none;
  `;
  notification.textContent = message;
  document.body.appendChild(notification);
  
  // Animate in
  gsap.to(notification, {
    opacity: 1,
    y: 0,
    duration: 0.3
  });
  
  // Animate out and remove
  setTimeout(() => {
    gsap.to(notification, {
      opacity: 0,
      y: -20,
      duration: 0.3,
      onComplete: () => notification.remove()
    });
  }, 2000);
}

// ================================
// LOADING SCREEN
// ================================
function hideLoadingScreen() {
  const loadingScreen = document.getElementById('loading-screen');
  if (!loadingScreen) return;
  
  setTimeout(() => {
    gsap.to(loadingScreen, {
      opacity: 0,
      duration: 0.6,
      onComplete: () => {
        loadingScreen.classList.add('hidden');
      }
    });
  }, 2000);
}

// ================================
// PARTICIPANT AVATARS ANIMATION
// ================================
function animateParticipants() {
  const avatars = document.querySelectorAll('.participant-avatar');
  
  avatars.forEach((avatar, index) => {
    avatar.addEventListener('mouseenter', function() {
      gsap.to(this, {
        scale: 1.2,
        duration: 0.3,
        ease: 'back.out(2)'
      });
    });
    
    avatar.addEventListener('mouseleave', function() {
      gsap.to(this, {
        scale: 1,
        duration: 0.3
      });
    });
    
    // Random floating animation
    gsap.to(avatar, {
      y: -5,
      duration: 2 + Math.random(),
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
      delay: index * 0.2
    });
  });
}

// ================================
// KEYBOARD SHORTCUTS
// ================================
function initKeyboardShortcuts() {
  document.addEventListener('keydown', (e) => {
    // H - Toggle hand
    if (e.key === 'h' || e.key === 'H') {
      const handBtn = document.querySelector('.control-btn:nth-child(1)');
      if (handBtn) handBtn.click();
    }
    
    // S - Toggle share
    if (e.key === 's' || e.key === 'S') {
      const shareBtn = document.querySelector('.control-btn:nth-child(2)');
      if (shareBtn) shareBtn.click();
    }
    
    // B - Toggle breakout
    if (e.key === 'b' || e.key === 'B') {
      const breakoutBtn = document.querySelector('.control-btn:nth-child(3)');
      if (breakoutBtn) breakoutBtn.click();
    }
    
    // Esc - Close modal or leave
    if (e.key === 'Escape') {
      if (state.hologramModalOpen) {
        closeHologramModal();
      } else {
        handleLeaveClassroom();
      }
    }
  });
}

// ================================
// INITIALIZATION
// ================================
function init() {
  console.log('🎓 Initializing Virtual Classroom...');
  
  // Initialize all features
  initClassroomScene();
  animateNeuralMetrics();
  initLearningTools();
  initControlButtons();
  initHologramModal();
  animateParticipants();
  initKeyboardShortcuts();
  hideLoadingScreen();
  
  // Add entry animations
  gsap.fromTo('.classroom-nav',
    { opacity: 0, y: -50 },
    { opacity: 1, y: 0, duration: 0.8, delay: 0.5 }
  );
  
  gsap.fromTo('.course-info',
    { opacity: 0, scale: 0.8 },
    { opacity: 1, scale: 1, duration: 0.8, delay: 0.7 }
  );
  
  gsap.fromTo('.neural-interface',
    { opacity: 0, x: -50 },
    { opacity: 1, x: 0, duration: 0.8, delay: 0.9 }
  );
  
  gsap.fromTo('.learning-tools',
    { opacity: 0, x: 50 },
    { opacity: 1, x: 0, duration: 0.8, delay: 1.1 }
  );
  
  gsap.fromTo('.control-bar',
    { opacity: 0, y: 50 },
    { opacity: 1, y: 0, duration: 0.8, delay: 1.3 }
  );
  
  console.log('✨ Virtual Classroom initialized!');
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export state for potential use
export { state };
